import { localPreferences } from "../browserStorage";
import { create } from "zustand";
import {
  User as FirebaseUser,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  signInWithCredential,
  UserCredential,
  GoogleAuthProvider,
  EmailAuthProvider,
  getIdTokenResult,
  signInWithEmailAndPassword,
  signInAnonymously,
  linkWithCredential,
  linkWithPopup,
  linkWithPhoneNumber,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
  OAuthCredential,
  UserInfo,
  AuthProvider,
} from "firebase/auth";
import {
  auth,
  googleProvider,
  appleProvider,
  db,
  firebaseConfig,
  changeUserRole,
} from "../firebase";
import { getDocument, newId } from "../firestore";
import {
  User,
  Account,
  WithRef,
  withRefOrUndefined,
  Mind,
} from "@um/contracts";
import {
  collection,
  doc,
  DocumentReference,
  getDoc,
  getDocs,
  limit,
  query,
  updateDoc,
  onSnapshot,
  Unsubscribe,
  setDoc,
} from "firebase/firestore";
import { logException, logUser, logUserProperty } from "../telemetry";
import { clearAttributionData } from "../utm";
import { useEffect, useMemo, useRef } from "react";
import { currentConfig } from "../appConfig";
import { combineSubscriptions, onSnapshotWithRetry } from "../utils";
import {
  AccountSettings,
  LocalSettings,
  Settings,
  DeviceSettings,
} from "@um/contracts";
import { createComputed } from "zustand-computed";
import { waitForState } from "../lib/utils";
import { deviceInfoService } from "../services/deviceInfoService";
import { NativeSignInFunction, NativeSignInResult } from "../appConfig";

export interface AuthStore {
  user: FirebaseUser | null;
  userData: WithRef<User> | undefined | null;
  selectedAccountId: string | null;
  loading: boolean;
  initializing: boolean;
  isAdmin: boolean;
  /** readOnlyAdmin claim — broad read access (e.g. admin portal viewing) without writes. */
  isReadOnlyAdmin: boolean;
  clinicianMode: boolean;
  wholesalerMode: boolean;
  defaultAccountId: string | null;
  account: WithRef<Account> | undefined | null;
  /** `Account/{id}/Settings/account` — subscribed when signed in. */
  accountSettings: AccountSettings | undefined | null;
  /**
   * The account `accountSettings` was loaded for. Compared against the current
   * (computed) `accountId` by `accountSettingsLoaded` so a previous account's
   * settings can never be mistaken for the current account's.
   */
  accountSettingsFor: string | null;
  userMindOverride: DocumentReference<Mind> | null;
  userMind: WithRef<Mind> | undefined | null;
  selectedMindRef: DocumentReference<Mind> | undefined;
  deviceSettings: DeviceSettings | undefined | null;
  selectedTestGroupId: string | null;

  // Actions
  signInWithGoogle: (dontCreateAccount?: boolean) => Promise<boolean>;
  signInWithApple: (dontCreateAccount?: boolean) => Promise<boolean>;
  signInWithEmail: (
    email: string,
    password: string,
    dontCreateAccount?: boolean
  ) => Promise<boolean>;
  signInAnonymously: () => Promise<boolean>;
  sendPhoneVerificationCode: (
    phoneNumber: string,
    recaptchaVerifier: RecaptchaVerifier
  ) => Promise<ConfirmationResult>;
  confirmPhoneCode: (
    confirmationResult: ConfirmationResult,
    code: string,
    options?: { dontCreateAccount?: boolean; wasLink?: boolean }
  ) => Promise<boolean>;
  logout: () => Promise<void>;
  setAccountId: (id: string | null) => void;
  switchAccount: (accountId: string) => void;
  setDefaultAccountId: (accountId: string) => Promise<void>;
  setUserMindOverride: (mindRef: DocumentReference<Mind> | null) => void;
  setSelectedMindRef: (mindRef: DocumentReference<Mind> | undefined) => void;
  setSelectedTestGroupId: (id: string | null) => void;
  updateSettings: (settings: Partial<Settings>) => Promise<void>; // Auto-routes to correct storage
  updateLocalSettings: (settings: Partial<LocalSettings>) => void;
  updateAccountSettings: (settings: Partial<AccountSettings>) => Promise<void>;
  updateDeviceSettings: (settings: Partial<DeviceSettings>) => Promise<void>;
  updateUserData: (data: Partial<User>) => Promise<void>;
  updateContactEmail: (email: string) => Promise<void>;
}

export interface AuthStateComputed {
  settings: Settings;
  accountId: string | null;
  /** See the computed block for why this is not just `accountSettings !== undefined`. */
  accountSettingsLoaded: boolean;
}

export interface InternalAuthStore {
  localSettings: LocalSettings;
  isHandlingCredentialCollision: boolean; // Prevents account subscription during credential collision handling
  setUser: (user: FirebaseUser | null) => void;
  setAccountData: (account: WithRef<Account> | undefined | null) => void;
  setUserMindData: (mind: WithRef<Mind> | undefined | null) => void;
  setUserData: (userData: WithRef<User> | undefined | null) => void;
  setLoading: (loading: boolean) => void;
  setInitializing: (initializing: boolean) => void;
  setClinicianMode: (value: boolean) => void;
  setWholesalerMode: (value: boolean) => void;
  // fetchOrCreateAccount: (user: FirebaseUser) => Promise<void>;
  setIsAdmin: (isAdmin: boolean) => void;
  setIsReadOnlyAdmin: (isReadOnlyAdmin: boolean) => void;
  /**
   * `forAccountId` records WHICH account these settings belong to. Callers
   * that load settings for a specific account must pass it — see
   * `accountSettingsLoaded` for why "not undefined" is not a safe readiness
   * signal on its own.
   */
  setAccountSettings: (
    settings: AccountSettings | undefined | null,
    forAccountId?: string | null
  ) => void;
  setDeviceSettings: (settings: DeviceSettings | undefined | null) => void;
  setIsHandlingCredentialCollision: (isHandling: boolean) => void;
}

const userMindOverrideKey = "userMindOverride";
const accountIdKey = "accountId";
const selectedTestGroupIdKey = "selectedTestGroupId";
const localSettingsKey = "localClientSettings"; // Renamed from "settings" to avoid conflicts with old acknowledgedNotifications
const deviceSettingsKey = "deviceSettings"; // For web browsers (non-native platforms)

// Helper to split settings by type using interface keys
// IMPORTANT: All AccountSettings fields must be listed here to route to Firestore
const accountSettingsKeys: (keyof AccountSettings)[] = [
  "watchedIntroVideo",
  "createdDefaultCharacter",
  "acknowledgedSupportingPerson",
  "enableRunNextScheduledButton",
  "enableDailyDigest",
  "v2CharactersEnabled",
  "onboardingProgress", // Flow state persistence - must be in Firestore for cross-device sync
  "feedbackStats",
  "migrated",
  "routinePaywallExperiment",
  "quests", // Tutorial ladder progress — cross-device, survives reinstall
  "dentalPilot", // Dental pilot cohort — drives dental framing, cross-device
  // Cadence for the two in-app dental prompts. Without these the writes fall
  // through to `console.warn("Unknown setting key")` and land in LOCAL
  // settings, so the prompts would re-fire forever with nothing to show why.
  "dentalCheckIn",
  "dentalFeedback",
];

const deviceSettingsKeys: (keyof DeviceSettings)[] = [
  "acknowledgedNotifications",
];

const localSettingsKeys: (keyof LocalSettings)[] = [
  // LocalSettings is currently empty, but kept for future use
];

function splitSettingsByType<T extends Record<string, any>>(
  settings: T
): {
  accountSettings: Partial<AccountSettings>;
  localSettings: any; // Use 'any' since LocalSettings might be empty
  deviceSettings: Partial<DeviceSettings>;
} {
  const accountSettings: Partial<AccountSettings> = {};
  const localSettings: any = {};
  const deviceSettings: Partial<DeviceSettings> = {};

  for (const [key, value] of Object.entries(settings)) {
    if (accountSettingsKeys.includes(key as keyof AccountSettings)) {
      (accountSettings as Record<string, unknown>)[key] = value;
    } else if (deviceSettingsKeys.includes(key as keyof DeviceSettings)) {
      deviceSettings[key as keyof DeviceSettings] = value;
    } else if (localSettingsKeys.includes(key as keyof LocalSettings)) {
      localSettings[key] = value;
    } else {
      // Unknown setting - log warning but add to local settings as fallback
      console.warn(
        `[AuthStore] Unknown setting key: ${key}, routing to local settings`
      );
      localSettings[key] = value;
    }
  }

  return { accountSettings, localSettings, deviceSettings };
}

// Initialize accountId from localStorage or URL query parameters
const getInitialAccountId = () => {
  // Check URL query parameters for accountId override
  const params = new URLSearchParams(window.location.search);
  const overrideAccountId = params.get("accountId");
  if (overrideAccountId) {
    return overrideAccountId;
  }

  const saved = localPreferences.getItem(accountIdKey);
  return saved ? saved : null;
};

const getInitialSelectedMindRef = (accountId: string) => {
  const mindId = localPreferences.getItem(`selectedMindId-${accountId}`);
  return mindId
    ? getDocument<Mind>(`Account/${accountId}/Mind/${mindId}`)
    : undefined;
};

// Initialize userMindOverride from localStorage
const getInitialUserMindOverride = (): DocumentReference<Mind> | null => {
  const savedMindPath = localPreferences.getItem(userMindOverrideKey);
  if (savedMindPath) {
    return getDocument<Mind>(savedMindPath);
  }
  return null;
};

// Initialize selectedTestGroupId from localStorage (persists across logout)
export const getPersistedTestGroupId = (): string | null => {
  if (typeof window === "undefined") return null;
  const saved = localPreferences.getItem(selectedTestGroupIdKey);
  return saved ? saved : null;
};

// remember the test group id when specified via the url
if (typeof window !== "undefined") {
  const urlTestGroupId = new URLSearchParams(window.location.search).get(
    "test"
  );
  if (urlTestGroupId !== null) {
    if (urlTestGroupId) {
      localPreferences.setItem(selectedTestGroupIdKey, urlTestGroupId);
    } else {
      localPreferences.removeItem(selectedTestGroupIdKey);
    }
  }
}

// One-time cleanup: sanitize any existing accountId in localStorage
if (typeof window !== "undefined") {
  const rawId = localPreferences.getItem(accountIdKey);
  if (rawId) {
    localPreferences.setItem(accountIdKey, rawId.trim());
  }
}

let usersUnsubscribe: Unsubscribe | null = null;
export const fetchOrCreateAccount = async (user: FirebaseUser) => {
  try {
    console.log(
      "[fetchOrCreateAccount] Starting account creation process for user:",
      user.uid
    );

    // Get attribution data if available
    const appCfg = currentConfig();
    const attributionData = appCfg.getAttributionData
      ? appCfg.getAttributionData()
      : {};

    const idToken = await user.getIdToken();
    console.log("[fetchOrCreateAccount] Got ID token, calling Cloud Function");

    const attributes = {
      paywallClass: "new", // accounts created after paywall was released
    };

    const testGroupId = getPersistedTestGroupId();
    const baseUrl =
      import.meta.env.VITE_USE_EMULATORS === "true"
        ? `http://localhost:5001/${firebaseConfig.projectId}/us-west1/getOrCreateAccount`
        : `https://us-west1-${firebaseConfig.projectId}.cloudfunctions.net/getOrCreateAccount`;
    const params = new URLSearchParams();
    if (testGroupId) {
      params.set("testGroupId", testGroupId);
    }
    // Client platform (web/ios/android) — lets the backend resolve the new
    // account's RevenueCat current offering (experiment enrollment + promo
    // grant) with the matching public API key at creation.
    params.set("platform", appCfg.getPlatform?.() ?? "web");
    const requestUrl = `${baseUrl}?${params.toString()}`;

    const response = await fetch(requestUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        ...attributionData,
        attributes,
      }),
    });

    // log the attributes for segmenting in GA
    Object.entries(attributes).forEach(([key, value]) => {
      logUserProperty(key, value);
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("[fetchOrCreateAccount] Cloud Function error:", {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
      });
      throw new Error(
        `Failed to fetch or create account: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    console.log("[fetchOrCreateAccount] Cloud Function response:", data);

    if (!data.account) {
      console.error("[fetchOrCreateAccount] No account ID in response:", data);
      throw new Error("No account ID returned from Cloud Function");
    }

    // Verify the account exists in Firestore
    const accountRef = doc(db, "Account", data.account);
    const accountDoc = await getDoc(accountRef);

    if (!accountDoc.exists()) {
      console.error(
        "[fetchOrCreateAccount] Account not found in Firestore:",
        data.account
      );
      throw new Error("Account not found in Firestore after creation");
    }

    console.log(
      "[fetchOrCreateAccount] Account created successfully:",
      data.account
    );
    // Only now is the stashed attribution spent. Clearing it on read (as this
    // used to) meant a failed or interrupted first attempt left the retry
    // with nothing, and the account was created with blank attribution.
    clearAttributionData();
    return data.account;
  } catch (error) {
    logException(error);
    console.error("[fetchOrCreateAccount] Error:", error);
    throw error;
  }
};

type AuthModes = { clinicianMode?: boolean; wholesalerMode?: boolean };

/** Resolve which default account ref applies for the active auth mode. */
function resolveDefaultAccountRef(
  userData:
    | Pick<
        User,
        | "defaultAccount"
        | "defaultClinicianAccount"
        | "defaultWholesalerAccount"
      >
    | null
    | undefined,
  modes: AuthModes
): DocumentReference<Account> | undefined {
  if (modes.wholesalerMode) return userData?.defaultWholesalerAccount;
  if (modes.clinicianMode) return userData?.defaultClinicianAccount;
  return userData?.defaultAccount;
}

function getDefaultAccountId(state: AuthStore): string | undefined {
  return resolveDefaultAccountRef(state.userData, state)?.id;
}

/**
 * "Have THIS account's settings arrived?"
 *
 * `accountSettings !== undefined` is not sufficient on its own. The effective
 * accountId is computed, so it flips the instant `userData.defaultAccount`
 * arrives, while `accountSettings` is only cleared by a React effect that runs
 * later. In that window the PREVIOUS account's settings (often `null`) are
 * still in the store, and `null !== undefined` reads as "loaded" — so a reader
 * concludes the account has no onboardingProgress and routes the user to Home
 * mid-onboarding (the web download-gate hand-off to the native app).
 *
 * Comparing against the account the settings were actually loaded for closes
 * that window without depending on effect ordering. Exported because
 * `waitForState` infers the bare store type, which excludes computed fields.
 */
export function selectAccountSettingsLoaded(state: AuthStore): boolean {
  const accountId = state.selectedAccountId || getDefaultAccountId(state);
  return (
    state.accountSettings !== undefined &&
    state.accountSettingsFor === (accountId ?? null)
  );
}

/**
 * Read the account the just-signed-in user ALREADY had.
 *
 * Must be called immediately after sign-in and BEFORE the invite-accept step
 * of collision recovery: `accept` runs `createOrUpdateUserIfNeeded`, which
 * repoints `User.defaultAccount` at the anonymous account whenever the user
 * doc needs any update (e.g. type flipping anonymous→user). Reading afterwards
 * would see the anonymous account and make the recovery below a no-op.
 */
async function readExistingDefaultAccountId(
  uid: string,
  modes: AuthModes
): Promise<string | undefined> {
  try {
    const userRef = doc(db, "User", uid) as DocumentReference<User>;
    const userData = (await getDoc(userRef)).data() as
      | Partial<User>
      | undefined;
    return resolveDefaultAccountRef(userData, modes)?.id;
  } catch (error) {
    logException(error);
    console.error(
      "[AuthStore] Failed to read existing default account before collision recovery:",
      error
    );
    return undefined;
  }
}

/**
 * Decide which account to land on after a credential-collision recovery.
 *
 * When linking an anonymous session to a credential that already belongs to
 * another Firebase user, the recovery path invites that user into the
 * ANONYMOUS account and signs them in there. They now own two accounts: the
 * anonymous one this session just created, and whatever account they already
 * had (captured by `readExistingDefaultAccountId` before the accept step).
 *
 * Keeping the anonymous account is right when it holds work the user just did
 * — web onboarding creates the character *before* sign-in, and switching away
 * would throw it away. But on a fresh app install the anonymous session is
 * empty, and staying on it strands the user on a blank duplicate while their
 * real account (with their character and onboarding progress) sits untouched
 * — that is UM-288. So: keep the anonymous account only if it has content,
 * otherwise fall back to the account they already had.
 */
async function resolveAccountAfterCredentialCollision(
  anonymousAccountId: string,
  existingDefaultAccountId: string | undefined
): Promise<string> {
  if (
    !existingDefaultAccountId ||
    existingDefaultAccountId === anonymousAccountId
  ) {
    return anonymousAccountId;
  }

  try {
    // Characters are the first thing onboarding creates, so their presence is
    // the earliest reliable signal that this account holds real work. Mirror
    // checkHasCharacters: fetch a small page unfiltered and drop hidden docs
    // client-side, since `isHidden` is absent on older records and a
    // where-filter would silently skip them.
    const animals = await getDocs(
      query(collection(db, `Account/${anonymousAccountId}/Animal`), limit(10))
    );
    const hasVisibleCharacter = animals.docs.some((d) => !d.data().isHidden);
    if (hasVisibleCharacter) {
      console.log(
        "[AuthStore] Anonymous account has content — keeping it after collision recovery",
        { anonymousAccountId, existingDefaultAccountId }
      );
      return anonymousAccountId;
    }

    console.log(
      "[AuthStore] Anonymous account is empty — switching to the user's existing account",
      { anonymousAccountId, existingDefaultAccountId }
    );
    return existingDefaultAccountId;
  } catch (error) {
    logException(error);
    console.error(
      "[AuthStore] Failed to resolve account after credential collision, staying put:",
      error
    );
    return anonymousAccountId;
  }
}

const computed = createComputed((state: AuthStore & InternalAuthStore) => {
  const accountId = state.selectedAccountId || getDefaultAccountId(state);
  return {
    settings: {
      ...(state.accountSettings || {}),
      ...state.localSettings,
      ...(state.deviceSettings || {}),
    },
    accountId,
    /** See {@link selectAccountSettingsLoaded}. */
    accountSettingsLoaded: selectAccountSettingsLoaded(state),
  } as AuthStateComputed;
});

const useAuthStoreinternal = create(
  computed((set, get) => {
    // Private function for handling post sign-in logic
    const handlePostSignIn = async (
      user: FirebaseUser,
      dontCreateAccount?: boolean
    ): Promise<boolean> => {
      if (!dontCreateAccount) {
        // First ensure user has an account (creates if needed)
        const createdAccountId = await fetchOrCreateAccount(user);

        // Check if user already has a preferred default account
        const userRef = doc(db, "User", user.uid) as DocumentReference<User>;
        const userData = (await getDoc(userRef)).data() as
          | Partial<User>
          | undefined;
        const { clinicianMode, wholesalerMode } = get();
        const defaultId = resolveDefaultAccountRef(userData, {
          clinicianMode,
          wholesalerMode,
        })?.id;
        const accountIdToUse = defaultId || createdAccountId;
        console.log(
          "[AuthStore] Using account:",
          accountIdToUse,
          defaultId
            ? `(user's default ${wholesalerMode ? "wholesaler" : clinicianMode ? "clinician" : ""})`
            : "(newly created/fetched)"
        );

        get().setAccountId(accountIdToUse);

        console.log("[AuthStore] Waiting for account data to be populated...");
        await waitForState(
          useAuthStoreinternal,
          ({ account }) => account !== undefined
        );
        console.log("[AuthStore] Account data populated successfully");
      } else {
        // If the user is not registered then we dont allow the user to sign in
        const userRef = doc(db, "User", user.uid) as DocumentReference<User>;
        const userData = (await getDoc(userRef)).data() as
          | Partial<User>
          | undefined;
        const { clinicianMode, wholesalerMode } = get();
        const defaultRef = resolveDefaultAccountRef(userData, {
          clinicianMode,
          wholesalerMode,
        });
        if (defaultRef) {
          get().setAccountId(defaultRef.id);
          console.log(
            "[AuthStore] Waiting for account data to be populated..."
          );
          await waitForState(
            useAuthStoreinternal,
            ({ account }) => account !== undefined
          );
          console.log("[AuthStore] Account data populated successfully");
        } else {
          return false;
        }
      }
      return true;
    };

    async function signInWithSocialProvider(options: {
      providerName: string;
      provider: AuthProvider;
      getCredential: (nativeCred: NativeSignInResult) => OAuthCredential;
      nativeSignIn?: NativeSignInFunction;
      dontCreateAccount?: boolean;
    }): Promise<boolean> {
      const {
        providerName,
        provider,
        getCredential,
        nativeSignIn,
        dontCreateAccount,
      } = options;
      const config = currentConfig();
      const { account: existingAccount } = get();
      const linkAccount = auth.currentUser?.isAnonymous;

      let cred: OAuthCredential | undefined;
      try {
        let result: UserCredential;

        if (config.isNativePlatform && nativeSignIn) {
          // Native (Capacitor) – we use the plugin and then bridge the
          // credential to the JS SDK so that Firestore/Functions are
          // authenticated.

          const credential = await nativeSignIn();
          if (!credential?.idToken) {
            throw new Error(
              `${providerName} sign-in failed: missing idToken from plugin`
            );
          }
          cred = getCredential(credential);

          if (linkAccount) {
            result = await linkWithCredential(auth.currentUser!, cred);
          } else {
            result = await signInWithCredential(auth, cred);
          }
        } else {
          // Web flow
          if (linkAccount) {
            result = await linkWithPopup(auth.currentUser!, provider);
          } else {
            result = await signInWithPopup(auth, provider);
          }
        }

        if (existingAccount && linkAccount) {
          const guestRole = existingAccount.userRoles?.find(
            (userRole) => !userRole.inviteEmail
          )!;

          // propogate the guest role mind to the new user
          await changeUserRole({
            action: "update",
            accountId: existingAccount.ref.id,
            inviteEmail: guestRole.inviteEmail || "",
            role: "owner",
            mindId: guestRole.mind?.id,
          });
        }

        return await handlePostSignIn(result.user, dontCreateAccount);
      } catch (error: any) {
        /** The existing user's credential, recovered from a failed link. */
        const collisionCredential = async () => {
          // when native hosted we should have a credential already from above
          if (!cred) {
            if (!error.customData?._tokenResponse?.oauthIdToken) {
              throw new Error("Unable to get credential for linked account");
            }
            // when web hosted we can get the credential from the token response on the exception
            return getCredential({
              idToken: error.customData._tokenResponse.oauthIdToken,
              accessToken: error.customData._tokenResponse.oauthAccessToken,
              // do we need nonce for IOS?
            });
          }
          if (
            config.getPlatform &&
            config.getPlatform() === "ios" &&
            provider === appleProvider
          ) {
            // IOS uses a nonce which prevents the idtoken from being used twice so we have to re-login the user
            console.log(
              "[AuthStore] Re-logging in user for IOS as we cant reuse the idtoken"
            );
            const credential = await nativeSignIn!();
            if (!credential?.idToken) {
              throw new Error(
                `${providerName} sign-in failed: missing idToken from plugin`
              );
            }
            return getCredential(credential);
          }
          return cred;
        };
        if (
          error.code === "auth/credential-already-in-use" &&
          linkAccount &&
          !existingAccount
        ) {
          // A guest session without an account has nothing to carry over:
          // sign straight in as the user who already owns this credential.
          console.log(
            `[AuthStore] ${providerName} already belongs to a user; leaving the empty guest session`
          );
          const result = await signInWithCredential(
            auth,
            await collisionCredential()
          );
          return await handlePostSignIn(result.user, dontCreateAccount);
        }
        if (
          error.code === "auth/credential-already-in-use" &&
          existingAccount
        ) {
          // This happens when a user tries to link an account with one that is already linked
          // in this case we just add the user to the account and sign them in
          try {
            // generate a code from the provider response token
            const userInfo: UserInfo = error.customData._tokenResponse;
            console.log(
              `[AuthStore] Account already linked. Adding linked account as owner: ${userInfo.email} from ${providerName}`
            );

            const tempEmail = newId();
            const {
              data: { inviteCode },
            } = await changeUserRole({
              action: "add",
              accountId: existingAccount.ref.id,
              inviteEmail: tempEmail,
              role: "owner",
            });

            cred = await collisionCredential();

            // Prevent account subscription from starting until the collision flow completes
            console.log(
              "[AuthStore] Setting isHandlingCredentialCollision to true"
            );
            get().setIsHandlingCredentialCollision(true);

            // sign in as the new user
            console.log("[AuthStore] Signing in with credential");
            await signInWithCredential(auth, cred);

            // Capture the account this user already had BEFORE accepting —
            // accept can repoint User.defaultAccount at the anonymous account.
            const { clinicianMode: cm, wholesalerMode: wm } = get();
            const priorAccountId = auth.currentUser
              ? await readExistingDefaultAccountId(auth.currentUser.uid, {
                  clinicianMode: cm,
                  wholesalerMode: wm,
                })
              : undefined;

            // accept the code
            console.log("[AuthStore] Accepting code");
            await changeUserRole({
              action: "accept",
              inviteCode,
            });

            // remove the guest user
            console.log("[AuthStore] Removing guest user");
            await changeUserRole({
              action: "remove",
              accountId: existingAccount.ref.id,
              inviteEmail: "",
            });

            // The user may already have had an account before this anonymous
            // session. Pick the right one BEFORE releasing the collision flag,
            // so the account subscription attaches to the final account rather
            // than flapping (UM-288).
            get().setAccountId(
              await resolveAccountAfterCredentialCollision(
                existingAccount.ref.id,
                priorAccountId
              )
            );

            // Allow account subscription to proceed now that the user has been added
            console.log(
              "[AuthStore] Setting isHandlingCredentialCollision to false"
            );
            get().setIsHandlingCredentialCollision(false);

            // dont call handlePostSignIn because we already have an account
            return true;
          } catch (innerError) {
            // Make sure to clear the flag on error too
            get().setIsHandlingCredentialCollision(false);
            logException(innerError);
            console.error(
              `Error handling add linked account with ${providerName}:`,
              error
            );
            throw error;
          }
        }
        // Error 12501 means the user cancelled the sign-in flow (or it was auto-dismissed
        // during Google Play Services initialization). This is not a real error.
        // Common Google Sign-In error codes:
        // - 12501: SIGN_IN_CANCELLED
        // - 12502: SIGN_IN_CURRENTLY_IN_PROGRESS
        // - 10: DEVELOPER_ERROR (wrong SHA-1 config)
        // - 7: NETWORK_ERROR
        const errorMessage = error?.message || "";
        const isCancelled =
          errorMessage.includes("12501") ||
          errorMessage.includes("cancelled") ||
          errorMessage.includes("canceled") ||
          error?.code === "auth/popup-closed-by-user";

        if (isCancelled) {
          console.log(
            `[AuthStore] ${providerName} sign-in was cancelled by user or auto-dismissed`
          );
          // Throw a specific error that callers can detect and handle gracefully
          const cancelError = new Error("Sign-in cancelled");
          (cancelError as any).code = "auth/cancelled";
          throw cancelError;
        }

        logException(error);
        console.error(`Error signing in with ${providerName}:`, error);
        throw error;
      }
    }

    const initialAccountId = getInitialAccountId();
    return {
      user: null,
      userData: undefined,
      selectedAccountId: initialAccountId,
      loading: true,
      initializing: true,
      isAdmin: false,
      isReadOnlyAdmin: false,
      clinicianMode: false,
      wholesalerMode: false,
      defaultAccountId: null,
      userMindOverride: getInitialUserMindOverride(),
      userMind: undefined,
      account: undefined,
      localSettings: (() => {
        const localSettings = localPreferences.getItem(localSettingsKey);
        return localSettings ? JSON.parse(localSettings) : {};
      })(),
      accountSettings: undefined,
      accountSettingsFor: null,
      deviceSettings: (() => {
        // For web browsers, load device settings from localStorage
        if (!currentConfig().isNativePlatform) {
          const stored = localPreferences.getItem(deviceSettingsKey);
          return stored ? JSON.parse(stored) : null;
        }
        return undefined; // Will be loaded from server for native
      })(),
      isHandlingCredentialCollision: false,
      selectedMindRef: initialAccountId
        ? getInitialSelectedMindRef(initialAccountId)
        : undefined,
      selectedTestGroupId: getPersistedTestGroupId(),

      setUser: (user) => set({ user }),
      setIsAdmin: (isAdmin) => set({ isAdmin }),
      setIsReadOnlyAdmin: (isReadOnlyAdmin) => set({ isReadOnlyAdmin }),
      setClinicianMode: (value) => set({ clinicianMode: value }),
      setWholesalerMode: (value) => set({ wholesalerMode: value }),
      setIsHandlingCredentialCollision: (isHandling) =>
        set({ isHandlingCredentialCollision: isHandling }),
      setUserData: (userData) => {
        const { clinicianMode, wholesalerMode } = get();
        const defaultId = userData
          ? (resolveDefaultAccountRef(userData, {
              clinicianMode,
              wholesalerMode,
            })?.id ?? null)
          : null;
        set({
          userData,
          defaultAccountId: defaultId,
        });
      },
      setAccountData: (account) => set({ account }),
      setUserMindData: (mind) => set({ userMind: mind }),
      setLoading: (loading) => set({ loading }),
      setInitializing: (initializing) => set({ initializing }),
      setAccountSettings: (settings, forAccountId = null) =>
        set({ accountSettings: settings, accountSettingsFor: forAccountId }),
      setDeviceSettings: (settings) => set({ deviceSettings: settings }),

      setAccountId: (id) => {
        console.log("[AuthInit] setAccountId", id);
        const sanitizedId = id ? id.trim() : null;
        // null indicates we have populated the selectedMindRef, undefined means it still needs to be populated
        const selectedMindRef = sanitizedId
          ? getInitialSelectedMindRef(sanitizedId)
          : undefined;
        set({
          selectedAccountId: sanitizedId,
          selectedMindRef: selectedMindRef, // null signals we have populated the
        });

        if (sanitizedId) {
          localPreferences.setItem(accountIdKey, sanitizedId);
        } else {
          localPreferences.removeItem(accountIdKey);
        }
      },

      setSelectedMindRef: (mindRef) => {
        set({ selectedMindRef: mindRef });
        // we dont delete the key because we always want something selected
        if (mindRef) {
          localPreferences.setItem(
            `selectedMindId-${mindRef.parent?.parent?.id}`,
            mindRef.id
          );
        }
      },

      setSelectedTestGroupId: (id) => {
        set({ selectedTestGroupId: id });
        if (id !== null) {
          localPreferences.setItem(selectedTestGroupIdKey, id);
        } else {
          localPreferences.removeItem(selectedTestGroupIdKey);
        }
      },

      updateAccountSettings: async (settings: Partial<AccountSettings>) => {
        const { accountId, accountSettings } = get();
        if (!accountId) {
          // Dropped writes here have caused hard-to-trace state loss (e.g.
          // onboarding progress) — make the drop visible.
          console.warn(
            "[AuthStore] updateAccountSettings dropped — no accountId",
            Object.keys(settings)
          );
          return;
        }

        // to avoid frequent database writes only update if the settings have changed
        const hasChanged = Object.entries<any>(settings).some(
          ([key, value]) =>
            !accountSettings || value !== (accountSettings as any)[key]
        );
        if (!hasChanged) {
          console.log(
            `[AuthInit] updateAccountSettings: no changes to account settings, skipping update of ${Object.keys(
              settings
            ).join(", ")}`
          );
          return;
        }

        const settingsRef = getDocument<AccountSettings>(
          `Account/${accountId}/Settings/account`
        );
        await setDoc(settingsRef, settings, { merge: true });

        // Patch against the store as it is NOW, not the snapshot taken before
        // the await. Two writers can be in flight at once (the experiment
        // stamper fires the moment uid+accountId exist, right when /explore is
        // stamping onboardingProgress and dentalPilot), and Firestore acks in
        // order, so whichever resolves last used to win the store with a
        // snapshot that predated the other's keys — Firestore held both, the
        // store held one, and anything reading the store once at flow start
        // (HybridFlowPage's dentalPilot seed) saw the field as absent.
        set((state) => ({
          accountSettings: { ...(state.accountSettings || {}), ...settings },
        }));
      },

      updateSettings: async (settings: Partial<Settings>) => {
        const { accountSettings, localSettings, deviceSettings } =
          splitSettingsByType(settings as any);

        if (Object.keys(accountSettings).length > 0) {
          console.log(
            "[AuthStore] Routing to account settings:",
            Object.keys(accountSettings)
          );
          await get().updateAccountSettings(accountSettings);
        }

        if (Object.keys(deviceSettings).length > 0) {
          console.log(
            "[AuthStore] Routing to device settings:",
            Object.keys(deviceSettings)
          );
          await get().updateDeviceSettings(deviceSettings);
        }

        if (Object.keys(localSettings).length > 0) {
          console.log(
            "[AuthStore] Routing to local settings:",
            Object.keys(localSettings)
          );
          get().updateLocalSettings(localSettings);
        }
      },

      updateLocalSettings: async (settings: Partial<LocalSettings>) => {
        // Only handles pure local settings (not device settings)
        let { localSettings } = get();
        localSettings = { ...localSettings, ...settings };
        localPreferences.setItem(
          localSettingsKey,
          JSON.stringify(localSettings)
        );
        set({ localSettings });
      },

      updateDeviceSettings: async (settings: Partial<DeviceSettings>) => {
        const { user } = get();
        if (!user) {
          console.warn(
            "[AuthStore] Cannot update device settings without user"
          );
          return;
        }

        const newSettings = {
          ...get().deviceSettings,
          ...settings,
        };

        // For web browsers, save to localStorage
        if (!currentConfig().isNativePlatform) {
          console.log(
            "[AuthStore] Saving device settings to localStorage (web platform)"
          );
          localPreferences.setItem(
            deviceSettingsKey,
            JSON.stringify(newSettings)
          );
          set({ deviceSettings: newSettings });
          return;
        }

        // For native apps, save to server
        try {
          console.log(
            "[AuthStore] Saving device settings to server (native platform)"
          );
          await deviceInfoService.updateDeviceSettings(settings, user.uid);

          // Optimistically update local state
          set({ deviceSettings: newSettings });
        } catch (error) {
          logException(error);
          console.error("[AuthStore] Error updating device settings:", error);
        }
      },

      updateUserData: async (data: Partial<User>) => {
        const { user, userData } = get();
        if (!user) {
          console.warn("[AuthStore] Cannot update user data without user");
          return;
        }

        try {
          const userRef = doc(db, "User", user.uid);
          await setDoc(userRef, data, { merge: true });

          // Optimistically update local state
          if (userData) {
            set({
              userData: { ...userData, ...data },
            });
          }
        } catch (error) {
          logException(error);
          console.error("[AuthStore] Error updating user data:", error);
        }
      },

      updateContactEmail: async (email: string) => {
        const { user, accountId, userData, account } = get();
        if (!user) {
          console.warn("[AuthStore] Cannot update contact email without user");
          return;
        }

        try {
          const userRef = doc(db, "User", user.uid);
          await setDoc(userRef, { contactEmail: email }, { merge: true });

          if (accountId) {
            const accountRef = doc(db, "Account", accountId);
            await setDoc(accountRef, { contactEmail: email }, { merge: true });
          }

          // Optimistically update local state
          if (userData) {
            set({
              userData: { ...userData, contactEmail: email },
            });
          }
          if (account) {
            set({
              account: { ...account, contactEmail: email },
            });
          }
        } catch (error) {
          logException(error);
          console.error("[AuthStore] Error updating contact email:", error);
          throw error;
        }
      },

      setDefaultAccountId: async (accountId: string) => {
        const { user, clinicianMode, wholesalerMode } = get();
        if (!user) {
          console.error("Cannot set default account, no user logged in.");
          return;
        }
        try {
          const userRef = doc(db, "User", user.uid);
          const accountRef = doc(db, "Account", accountId);
          await updateDoc(userRef, {
            ...(wholesalerMode
              ? { defaultWholesalerAccount: accountRef }
              : clinicianMode
                ? { defaultClinicianAccount: accountRef }
                : { defaultAccount: accountRef }),
          });
          set({ defaultAccountId: accountId });
        } catch (error) {
          logException(error);
          console.error("Error setting default account:", error);
          throw error;
        }
      },

      setUserMindOverride: (mindRef: DocumentReference<Mind> | null) => {
        // Clear the loaded mind alongside the override: `role` flips
        // synchronously on override change, so pages swap immediately — a
        // stale `userMind` would render the PREVIOUS scope's data until the
        // new snapshot lands. `undefined` (not null) keeps the
        // `userMindLoaded` init gate reporting "not loaded yet".
        set({ userMindOverride: mindRef, userMind: undefined });

        if (mindRef) {
          localPreferences.setItem(userMindOverrideKey, mindRef.path);
        } else {
          localPreferences.removeItem(userMindOverrideKey);
        }
      },

      switchAccount: (accountId: string) => {
        get().setAccountId(accountId);
        // Optional: Reload necessary data for the new account
      },

      signInWithGoogle: async (dontCreateAccount?: boolean): Promise<boolean> =>
        signInWithSocialProvider({
          providerName: "Google",
          provider: googleProvider,
          getCredential: (credential) =>
            GoogleAuthProvider.credential(
              credential.idToken,
              credential.accessToken ?? undefined
            ),
          nativeSignIn: currentConfig().nativeAuthentication?.signInWithGoogle,
          dontCreateAccount,
        }),

      signInWithApple: async (dontCreateAccount?: boolean): Promise<boolean> =>
        signInWithSocialProvider({
          providerName: "Apple",
          provider: appleProvider,
          getCredential: (credential) =>
            appleProvider.credential({
              idToken: credential.idToken,
              rawNonce: credential.nonce ?? undefined,
            }),
          nativeSignIn: currentConfig().nativeAuthentication?.signInWithApple,
          dontCreateAccount,
        }),

      signInWithEmail: async (
        email: string,
        password: string,
        dontCreateAccount?: boolean
      ): Promise<boolean> => {
        const { account: existingAccount } = get();
        const linkAccount = auth.currentUser?.isAnonymous;

        try {
          let result: UserCredential;

          if (linkAccount) {
            // Link anonymous account with email credential
            console.log(
              "[AuthStore] Linking anonymous account with email credential"
            );
            const credential = EmailAuthProvider.credential(email, password);
            result = await linkWithCredential(auth.currentUser!, credential);

            // Propagate the guest role mind to the new user (same as social providers)
            if (existingAccount) {
              const guestRole = existingAccount.userRoles?.find(
                (userRole) => !userRole.inviteEmail
              );
              if (guestRole) {
                await changeUserRole({
                  action: "update",
                  accountId: existingAccount.ref.id,
                  inviteEmail: guestRole.inviteEmail || "",
                  role: "owner",
                  mindId: guestRole.mind?.id,
                });
              }
            }
          } else {
            // Normal email sign-in
            result = await signInWithEmailAndPassword(auth, email, password);
          }

          return await handlePostSignIn(result.user, dontCreateAccount);
        } catch (error: any) {
          if (
            (error.code === "auth/credential-already-in-use" ||
              error.code === "auth/email-already-in-use") &&
            linkAccount &&
            !existingAccount
          ) {
            // A guest session without an account has nothing to carry over:
            // sign straight in as the user who already owns this email.
            console.log(
              "[AuthStore] Email already belongs to a user; leaving the empty guest session"
            );
            const result = await signInWithEmailAndPassword(
              auth,
              email,
              password
            );
            return await handlePostSignIn(result.user, dontCreateAccount);
          }
          // Handle credential-already-in-use or email-already-in-use (user already has an account with this email)
          // - auth/credential-already-in-use: credential is linked to another Firebase user
          // - auth/email-already-in-use: email is already registered (from linkWithCredential calling signUp)
          if (
            (error.code === "auth/credential-already-in-use" ||
              error.code === "auth/email-already-in-use") &&
            existingAccount &&
            linkAccount
          ) {
            // This happens when a user tries to link an account with one that is already linked
            // in this case we add the existing user to the anonymous account and sign them in
            try {
              console.log(
                `[AuthStore] Email already linked to another account. Adding linked account as owner: ${email}`
              );

              // Create a temp invite for the anonymous account
              const tempEmail = newId();
              const {
                data: { inviteCode },
              } = await changeUserRole({
                action: "add",
                accountId: existingAccount.ref.id,
                inviteEmail: tempEmail,
                role: "owner",
              });

              // Prevent account subscription from starting until the collision flow completes
              console.log(
                "[AuthStore] Setting isHandlingCredentialCollision to true"
              );
              get().setIsHandlingCredentialCollision(true);

              // Sign in as the existing user
              console.log("[AuthStore] Signing in with email credential");
              await signInWithEmailAndPassword(auth, email, password);

              // Capture the account this user already had BEFORE accepting —
              // accept can repoint User.defaultAccount at the anonymous
              // account.
              const { clinicianMode: cm, wholesalerMode: wm } = get();
              const priorAccountId = auth.currentUser
                ? await readExistingDefaultAccountId(auth.currentUser.uid, {
                    clinicianMode: cm,
                    wholesalerMode: wm,
                  })
                : undefined;

              // Accept the invite to gain access to the anonymous account
              console.log("[AuthStore] Accepting code");
              await changeUserRole({
                action: "accept",
                inviteCode,
              });

              // Remove the guest user role
              console.log("[AuthStore] Removing guest user");
              await changeUserRole({
                action: "remove",
                accountId: existingAccount.ref.id,
                inviteEmail: "",
              });

              // The user may already have had an account before this anonymous
              // session. Pick the right one BEFORE releasing the collision
              // flag, so the account subscription attaches to the final
              // account rather than flapping (UM-288).
              get().setAccountId(
                await resolveAccountAfterCredentialCollision(
                  existingAccount.ref.id,
                  priorAccountId
                )
              );

              // Allow account subscription to proceed now that the user has been added
              console.log(
                "[AuthStore] Setting isHandlingCredentialCollision to false"
              );
              get().setIsHandlingCredentialCollision(false);

              // Don't call handlePostSignIn because we already have an account
              return true;
            } catch (innerError) {
              // Make sure to clear the flag on error too
              get().setIsHandlingCredentialCollision(false);
              logException(innerError);
              console.error(
                "Error handling add linked account with email:",
                innerError
              );
              throw error;
            }
          }

          logException(error);
          console.error("Error signing in with email:", error);
          throw error;
        }
      },

      signInAnonymously: async (): Promise<boolean> => {
        try {
          const result = await signInAnonymously(auth);
          return await handlePostSignIn(result.user);
        } catch (error) {
          logException(error);
          console.error("Error signing in anonymously:", error);
          throw error;
        }
      },

      sendPhoneVerificationCode: async (
        phoneNumber: string,
        recaptchaVerifier: RecaptchaVerifier
      ): Promise<ConfirmationResult> => {
        const linkAccount = auth.currentUser?.isAnonymous;
        if (linkAccount) {
          return await linkWithPhoneNumber(
            auth.currentUser!,
            phoneNumber,
            recaptchaVerifier
          );
        }
        return await signInWithPhoneNumber(
          auth,
          phoneNumber,
          recaptchaVerifier
        );
      },

      confirmPhoneCode: async (
        confirmationResult: ConfirmationResult,
        code: string,
        options?: { dontCreateAccount?: boolean; wasLink?: boolean }
      ): Promise<boolean> => {
        const { account: existingAccount } = get();
        const wasLink = options?.wasLink ?? false;
        const result = await confirmationResult.confirm(code);
        if (wasLink && existingAccount) {
          const guestRole = existingAccount.userRoles?.find(
            (userRole) => !userRole.inviteEmail
          );
          if (guestRole) {
            await changeUserRole({
              action: "update",
              accountId: existingAccount.ref.id,
              inviteEmail: guestRole.inviteEmail || "",
              role: "owner",
              mindId: guestRole.mind?.id,
            });
          }
        }
        return await handlePostSignIn(result.user, options?.dontCreateAccount);
      },

      logout: async () => {
        try {
          // Sign out from Firebase
          await signOut(auth);
          // Sign out from native Google/Apple session to force account picker on next login
          const nativeAuth = currentConfig().nativeAuthentication;
          if (nativeAuth?.signOut) {
            await nativeAuth.signOut();
          }
          set({
            selectedAccountId: null,
            userData: null,
            userMindOverride: null,
          });
          // preserve the selected test group id and email gate preferences during logout
          const selectedTestGroupId = localPreferences.getItem(
            selectedTestGroupIdKey
          );
          const appleEmailGatePreferences: Record<string, string> = {};
          for (let i = 0; i < localPreferences.length; i++) {
            const key = localPreferences.key(i);
            if (
              key &&
              (key.startsWith("apple-email-gate-dismissed-") ||
                key.startsWith("apple-email-gate-dont-ask-"))
            ) {
              const value = localPreferences.getItem(key);
              if (value) {
                appleEmailGatePreferences[key] = value;
              }
            }
          }

          localPreferences.clear();

          if (selectedTestGroupId) {
            localPreferences.setItem(
              selectedTestGroupIdKey,
              selectedTestGroupId
            );
          }
          Object.entries(appleEmailGatePreferences).forEach(([key, val]) => {
            localPreferences.setItem(key, val);
          });
        } catch (error) {
          logException(error);
          console.error("Error signing out:", error);
          throw error;
        }
      },

      // Internal method for fetching or creating account
    };
  })
);

// export just the public facing api
export const useAuthStore = useAuthStoreinternal;

// Hook to initialize Firebase auth listener
export function useAuthInit(clinicianMode?: boolean, wholesalerMode?: boolean) {
  const {
    setUser,
    setLoading,
    setInitializing,
    setUserData,
    setAccountId,
    setIsAdmin,
    setIsReadOnlyAdmin,
    setClinicianMode,
    setWholesalerMode,
    accountId,
    setUserMindData,
    setAccountData,
    user,
    userData,
    account,
    userMind,
    userMindOverride,
    accountSettings,
    setAccountSettings,
    deviceSettings,
    setDeviceSettings,
    isHandlingCredentialCollision,
  } = useAuthStoreinternal();

  const lastLoggedUserTypeRef = useRef<string | null>(null);
  const lastSubscribedAccountIdRef = useRef<string | null>(null);

  useEffect(() => {
    setClinicianMode(clinicianMode ?? false);
  }, [clinicianMode, setClinicianMode]);

  useEffect(() => {
    setWholesalerMode(wholesalerMode ?? false);
  }, [wholesalerMode, setWholesalerMode]);

  useEffect(() => {
    console.log("[AuthInit] useEffect running. Setting up auth listener.");
    const initializeAuthListener = async (): Promise<() => void> => {
      try {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
          console.log(
            "[AuthInit] onAuthStateChanged callback fired. User:",
            user?.uid
          );
          const previousUid = useAuthStoreinternal.getState().user?.uid;
          setUser(user);

          if (!user) {
            console.log(
              "[AuthInit] User is null. Clearing accountId, userData, userAgent."
            );
            setAccountId(null);
            setUserData(null);
            setUserMindData(null);
            setAccountData(null);
            setAccountSettings(null);

            // For web, clear device settings from localStorage
            if (!currentConfig().isNativePlatform) {
              localPreferences.removeItem(deviceSettingsKey);
            }
            setDeviceSettings(null);

            // User is not authenticated, safe to set initializing to false
            setInitializing(false);
          } else {
            // Only reset loaded state when the signed-in USER actually
            // changed. The callback also fires for the same user (listener
            // re-attach, StrictMode double-mount, auth internals) — and a
            // wipe then is destructive: the Firestore listeners only emit on
            // server-side CHANGES, so account/settings would stay `undefined`
            // until the next write. That frozen state made saved onboarding
            // progress invisible and restarted onboarding at step 0.
            if (previousUid !== user.uid) {
              setUserData(undefined);
              setUserMindData(undefined);
              setAccountData(undefined);
              setAccountSettings(undefined);
              setDeviceSettings(undefined);
              console.log("[AuthInit] Setting initializing to true");
              setInitializing(true);
            } else {
              console.log(
                "[AuthInit] Same user re-reported, keeping loaded account state"
              );
            }

            console.log("[AuthInit] User found. Fetching accounts.");
            logUser(user.uid, user.email ?? "");

            const tokenResult = await getIdTokenResult(user);
            setIsAdmin(tokenResult.claims.admin === true);
            setIsReadOnlyAdmin(tokenResult.claims.readOnlyAdmin === true);

            if (tokenResult.claims.testResourceGroup) {
              logUserProperty(
                "test_group",
                tokenResult.claims.testResourceGroup as string
              );
            }
          }
        });

        setLoading(false);
        return unsubscribe;
      } catch (error) {
        logException(error);
        console.error("[AuthInit] Error in initializeAuthListener:", error);
        console.log("[AuthInit] Calling setLoading(false) due to error.");
        setLoading(false);
        setInitializing(false);
        return () => {};
      }
    };

    const unsubscribePromise = initializeAuthListener();

    return () => {
      unsubscribePromise.then((unsubscribe) => unsubscribe());
    };
  }, [
    setUser,
    setLoading,
    setInitializing,
    setUserData,
    setAccountId,
    setUserMindData,
    setAccountData,
    setAccountSettings,
    setDeviceSettings,
  ]);

  // subscription to get Account data
  //
  // Re-subscribes when the auth identity changes (user?.uid dep): the auth
  // listener wipes account/settings state on a genuine user change, and
  // since Firestore listeners only emit on server-side changes, an already-
  // attached subscription would never repopulate the wiped values. A fresh
  // subscribe re-delivers the current documents immediately.
  const authUid = user?.uid;
  useEffect(() => {
    // Don't subscribe during credential collision handling - wait until the flow completes
    if (isHandlingCredentialCollision) {
      console.log(
        "[AuthInit] Skipping account subscription - credential collision in progress"
      );
      return;
    }

    if (accountId) {
      console.log(
        "[AuthInit] AccountId changed, subscribing to account and settings",
        accountId
      );

      // Switching accounts: drop the previous account's data immediately so
      // consumers see "loading" (undefined) instead of the OLD account's
      // account/settings while the new subscription attaches. Without this,
      // readers like the onboarding flow can act on another account's
      // settings during the switch window.
      if (
        lastSubscribedAccountIdRef.current &&
        lastSubscribedAccountIdRef.current !== accountId
      ) {
        setAccountData(undefined);
        setAccountSettings(undefined);
      }
      lastSubscribedAccountIdRef.current = accountId;

      return combineSubscriptions(
        // account
        onSnapshotWithRetry(
          getDocument<Account>(`Account/${accountId}`),
          (doc) => setAccountData(doc || null),
          (error) => {
            logException(error);
            console.error(
              "[AuthInit] Error subscribing to account. Resetting accountId to fallback on default account",
              error
            );
            // this will fall back on the default subscription
            setAccountId(null);
          }
        ),
        // settings — subscribe to the DOCUMENT, not a documentId()-filtered
        // query. The query subscription missed the doc's CREATION when it
        // attached before the first settings write (true for every brand-new
        // account), leaving accountSettings frozen at null — onboarding
        // progress writes landed in Firestore but the store never saw them,
        // so reloads restarted onboarding at step 0. (The unused
        // `reconnectAccountSettings` flag was an earlier signal of this same
        // defect.) A document listener delivers creates/updates/deletes.
        onSnapshotWithRetry(
          getDocument<AccountSettings>(`Account/${accountId}/Settings/account`),
          // Stamp WHICH account this snapshot is for. Readers compare it to
          // the current accountId so a stale snapshot from the previously
          // selected account can never read as "this account has no settings".
          (doc) => setAccountSettings(doc || null, accountId),
          undefined,
          undefined,
          // Server-confirmed snapshots only, until the first one lands. A
          // write to this doc before it has been fetched (the experiment
          // stamp fires the moment uid + accountId exist) echoes back a
          // partial view holding just the written field; taking that as
          // "settings loaded" made AppInitializer decide onboarding on a
          // document without `onboardingProgress` — so a user resuming web
          // onboarding in the app was sent Home with no Mind. Offline starts
          // are covered by the initializer's own timeout, which routes to
          // /onboarding where the flow self-corrects.
          { waitForServer: true }
        )
      );
    }
  }, [accountId, authUid, setAccountId, isHandlingCredentialCollision]);

  // subscription to get User data
  useEffect(() => {
    if (!user) {
      lastLoggedUserTypeRef.current = null;
      return;
    }
    console.log(
      "[AuthInit] user changed, subscribing to users for account:",
      user.uid
    );
    return onSnapshot(
      getDocument<User>(`User/${user.uid}`),
      async (doc) => {
        const userData = withRefOrUndefined(doc) || null;
        setUserData(userData);
        if (userData?.type) {
          const key = user.uid + "|" + userData.type;
          if (lastLoggedUserTypeRef.current !== key) {
            logUserProperty("user_type", userData.type);
            lastLoggedUserTypeRef.current = key;
          }
        }
      },
      (error) => {
        console.error("[AuthInit] Error subscribing to user:", error);
        setUserData(null);
      }
    );
  }, [user, setUserData]);

  // subscription to get device settings
  useEffect(() => {
    if (user) {
      // For web browsers, device settings come from localStorage (already loaded in initial state)
      if (!currentConfig().isNativePlatform) {
        console.log(
          "[AuthInit] Using localStorage for device settings (web platform)"
        );
        // Already loaded from localStorage in initial state, just set to null if not present
        const stored = localPreferences.getItem(deviceSettingsKey);
        setDeviceSettings(stored ? JSON.parse(stored) : null);
        return;
      }

      // For native apps, subscribe to server device settings
      console.log(
        "[AuthInit] Setting up device settings subscription for user:",
        user.uid
      );

      // Initialize device reference asynchronously
      deviceInfoService
        .initializeDeviceRef(user.uid)
        .then((deviceRef) => {
          if (!deviceRef) {
            console.log("[AuthInit] No device reference (not native platform)");
            setDeviceSettings(null);
            return;
          }

          // Subscribe to device settings
          return onSnapshot(
            deviceRef,
            (doc) => {
              if (doc.exists()) {
                const deviceData = doc.data();
                setDeviceSettings(deviceData.settings || null);
                console.log("[AuthInit] Device settings updated:", {
                  acknowledgedNotifications:
                    deviceData?.settings?.acknowledgedNotifications,
                });
              } else {
                setDeviceSettings(null);
              }
            },
            (error) => {
              console.error(
                "[AuthInit] Error subscribing to device settings:",
                error
              );
              setDeviceSettings(null);
            }
          );
        })
        .catch((error) => {
          console.error(
            "[AuthInit] Error initializing device reference:",
            error
          );
          setDeviceSettings(null);
        });
    } else {
      setDeviceSettings(undefined);
    }
  }, [user, setDeviceSettings]);

  // calculate and cache the current User Mind reference
  const userMindRef = useMemo(() => {
    if (userMindOverride) {
      return userMindOverride;
    }
    const accuntRole = account?.userRoles?.find(
      (role) => role.agent?.user?.id === user?.uid
    );
    if (accuntRole) {
      return accuntRole.mind || null;
    }
    return account ? null : undefined;
  }, [userMindOverride, account, user]);

  // subscribe to the current User Mind data
  useEffect(() => {
    if (userMindRef) {
      console.log("[AuthInit] userMindRef has changed to ", userMindRef.path);
      return onSnapshot(
        userMindRef,
        async (doc) => setUserMindData(withRefOrUndefined(doc) || null),
        (error) => {
          console.error("[AuthInit] Error subscribing to userMindRef:", error);
          setUserMindData(null);
        }
      );
    } else if (userMindRef === null) {
      console.log("[AuthInit] userMindRef is null");
      setUserMindData(null);
    }
  }, [userMindRef, setUserMindData]);

  // When everything is ready complete the initialization
  useEffect(() => {
    // Complete initialization when:
    // 1. userData is loaded (not undefined)
    // 2. If accountId is set, account and account settings must be loaded
    // 3. If userMindRef exists, userMind must be loaded
    // 4. Device settings are loaded (not undefined)
    const userDataLoaded = userData !== undefined;
    const accountLoaded =
      !accountId || (account !== undefined && accountSettings !== undefined);
    const userMindLoaded = userMindRef === undefined || userMind !== undefined;
    const deviceLoaded = deviceSettings !== undefined;

    const isComplete =
      userDataLoaded && accountLoaded && userMindLoaded && deviceLoaded;
    console.log(
      `[AuthInit] Initializing ${isComplete ? "complete" : "incomplete"}`,
      {
        userData: userData ? "set" : userData,
        account: account ? "set" : account,
        userMind: userMind ? "set" : userMind,
        accountSettings: accountSettings ? "set" : accountSettings,
        deviceSettings: deviceSettings ? "set" : deviceSettings,
        userMindRef,
        accountId,
      }
    );
    if (isComplete) {
      // Now that user data is loaded (or confirmed not to exist), set initializing to false
      setInitializing(false);
    }
  }, [
    userData,
    account,
    userMind,
    userMindRef,
    accountId,
    accountSettings,
    deviceSettings,
    setInitializing,
  ]);
}
